import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import { useDispatch, useSelector } from 'react-redux';
import * as actions from '../../redux/actions';
import api from '../../utils/functions/api';
import { useSocket } from '../../contexts/SocketContext';
import { ProjectDashboardTable } from '../../components/Table';

// แท็บ "โครงการภายใน" ของหน้า /project/project — แสดงโปรเจกต์ที่ลูกค้า (Contact) อยู่ในรายการที่เลือกไว้
// รายการลูกค้าเก็บใน DB ที่ Information.internal_project_clients (ตั้งค่าเดียวทั้งระบบ ไม่ใช่ต่อผู้ใช้)
export default function InternalProjectsTab({
  infoId,
  clientIds,
  onClientIdsSaved,
  isAdmin,
  pageScale,
  onClickProject,
  onEditProject,
  onDeleteProject,
}) {
  const dispatch = useDispatch();
  const contacts = useSelector((state) => state.contact?.rows || []);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [draft, setDraft] = useState([]);
  const [saving, setSaving] = useState(false);
  // ตัวกรองของตารางในแท็บนี้แยกจากแท็บ "ตารางโครงการ" (ไม่ใช้ตัวกรองมีภาระผูกพัน/JS_ เป็นค่าเริ่มต้น)
  const [statusFilter, setStatusFilter] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [projectFilter, setProjectFilter] = useState('');

  const socket = useSocket();
  const clientIdsRef = useRef(clientIds);
  clientIdsRef.current = clientIds;

  const fetchRows = useCallback(async (ids, silent = false) => {
    if (!silent) setLoading(true);
    try {
      // internal=1: ลูกค้าอยู่ในรายการ OR โครงการที่ติ๊ก "โครงการภายใน" ตอนสร้าง/แก้ไข
      const params = new URLSearchParams({
        size: 300,
        page: 1,
        skip_ts: '1',
        slim_expenses: '1',
        internal: '1',
        client_ids: ids.join(','),
      });
      const res = await api.get(`${process.env.REACT_APP_API_URL}/project?${params}`, {
        __silentError: silent,
      });
      setRows(res.data?.rows || []);
    } catch (e) {
      console.error('internal projects fetch error', e);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  // เห็นการแก้จากที่อื่นเอง (โครงการ/งบ/ค่าใช้จ่าย/จ่ายเงินเปลี่ยน) — โหลดใหม่เงียบๆ แบบ debounce
  useEffect(() => {
    if (!socket) return undefined;
    let timer = null;
    const reload = () => {
      clearTimeout(timer);
      timer = setTimeout(() => fetchRows(clientIdsRef.current, true), 2000);
    };
    const events = [
      'project:created',
      'project:updated',
      'budget:updated',
      'expenses:created',
      'expenses:updated',
      'expenses:deleted',
      'payout:created',
      'payout:updated',
      'payout:deleted',
    ];
    events.forEach((ev) => socket.on(ev, reload));
    return () => {
      clearTimeout(timer);
      events.forEach((ev) => socket.off(ev, reload));
    };
  }, [socket, fetchRows]);

  useEffect(() => {
    dispatch(actions.contactAll({}));
  }, [dispatch]);

  useEffect(() => {
    fetchRows(clientIds);
  }, [clientIds, fetchRows]);

  const contactById = useMemo(() => {
    const map = new Map();
    contacts.forEach((c) => map.set(String(c._id), c));
    return map;
  }, [contacts]);

  const selectedContacts = useMemo(
    () => clientIds.map((id) => contactById.get(id) || { _id: id, name: id }),
    [clientIds, contactById],
  );

  const openPicker = () => {
    setDraft(selectedContacts);
    setPickerOpen(true);
  };

  const handleSave = async () => {
    if (!infoId) return;
    const ids = draft.map((c) => String(c._id));
    setSaving(true);
    try {
      await api.put(`${process.env.REACT_APP_API_URL}/information/${infoId}`, {
        internal_project_clients: ids,
      });
      onClientIdsSaved(ids);
      setPickerOpen(false);
    } catch (e) {
      console.error('internal projects save error', e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<GroupsIcon sx={{ fontSize: '0.9rem !important' }} />}
          onClick={openPicker}
          disabled={!infoId}
          sx={{ borderRadius: 1.5, height: 25.2, fontSize: '0.7rem', whiteSpace: 'nowrap' }}
        >
          เลือกลูกค้า ({clientIds.length})
        </Button>
        {selectedContacts.map((c) => (
          <Chip key={c._id} size="small" label={c.name} />
        ))}
        {!clientIds.length && (
          <Typography variant="caption" color="text.secondary">
            ยังไม่ได้เลือกลูกค้า — กด "เลือกลูกค้า" เพื่อกำหนดว่าลูกค้ารายไหนเป็นโครงการภายใน
          </Typography>
        )}
      </Stack>

      <Card sx={{ borderRadius: 0, boxShadow: '0 2px 10px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
        <ProjectDashboardTable
          projects={{ rows, total: rows.length }}
          isLoading={loading}
          isAdmin={isAdmin}
          pageScale={pageScale}
          onClickProject={onClickProject}
          onEditProject={onEditProject}
          onDeleteProject={onDeleteProject}
          typeFilter={[]}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          customerFilter={customerFilter}
          onCustomerFilterChange={setCustomerFilter}
          projectFilter={projectFilter}
          onProjectFilterChange={setProjectFilter}
        />
      </Card>

      <Dialog open={pickerOpen} onClose={() => !saving && setPickerOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>เลือกลูกค้าที่เป็นโครงการภายใน</DialogTitle>
        <DialogContent>
          <Autocomplete
            multiple
            disableCloseOnSelect
            sx={{ mt: 1 }}
            options={contacts}
            value={draft}
            onChange={(_e, v) => setDraft(v)}
            getOptionLabel={(c) => c?.name || ''}
            isOptionEqualToValue={(opt, val) => String(opt._id) === String(val._id)}
            renderInput={(params) => <TextField {...params} label="ลูกค้า" size="small" />}
            noOptionsText="ไม่พบลูกค้า"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPickerOpen(false)} disabled={saving}>ยกเลิก</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>บันทึก</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
